import { User } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { useUser } from '../../../queries/users'
import Avatar from '../../core/Avatar'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Column, VerticalCenteredRow } from '../../core/Components'
import { prettyPrintWalletAddress } from '../../core/Utils'
import { gray1, gray10, gray4 } from '../../platform/color'
import { CurrentlyPlayingContext, CurrentlyPlayingTypes } from '../Studio'
import { ALLOW_STEM_AND_SONG_PREVIEW } from '../StudioActivityBar'
import SampleList from './sample-list'
import StemItem from './StemItem'

interface SongItemProps {
  songData: IArpMedia
  hideBottomBorder?: boolean
}

interface ArtistMediaDropdownProps {
  title?: string
  artistProfile: User
  artistEthAddress: string
  subtitle?: string
  helperText?: string
  song?: IArpMedia
  hideBottomBorder?: boolean
}

const ArtistMediaDropdown: FC<PropsWithChildren<ArtistMediaDropdownProps>> = ({
  title,
  artistProfile,
  artistEthAddress,
  subtitle,
  helperText,
  hideBottomBorder,
  song,
  children
}) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useContext(CurrentlyPlayingContext)
  const [hover, setHover] = useState<boolean>(false)
  const isPlaying =
    song &&
    currentlyPlaying.type === CurrentlyPlayingTypes.SONG &&
    currentlyPlaying.index === song.id
  const artistProfileLoaded = artistProfile !== undefined

  const artistDisplay = artistProfile
    ? artistProfile.displayName
    : prettyPrintWalletAddress(artistEthAddress)

  return (
    <Column css={{ margin: '8px 0px', borderBottom: !hideBottomBorder && `1px solid ${gray10}` }}>
      <VerticalCenteredRow
        className={ALLOW_STEM_AND_SONG_PREVIEW ? 'cursor-pointer' : ''}
        title={title || artistDisplay}
        style={{ flex: '1' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() =>
          song &&
          ALLOW_STEM_AND_SONG_PREVIEW &&
          setCurrentlyPlaying(CurrentlyPlayingTypes.SONG, song)
        }
      >
        {/* Image */}
        <VerticalCenteredRow
          style={{
            height: '40px',
            width: '40px',
            minWidth: '40px',
            minHeight: '40px'
          }}
        >
          {artistProfileLoaded ? (
            <div style={{ height: '100%', width: '100%' }}>
              <Avatar imageUrl={artistProfile?.profilePicture?.url} size="100%" />
              {song && ALLOW_STEM_AND_SONG_PREVIEW && (hover || isPlaying) && (
                <img
                  src={`/static/icons/${
                    isPlaying ? 'studio_pause_circle' : 'studio_play_circle'
                  }.svg`}
                  style={{
                    position: 'relative',
                    top: '-40px',
                    width: '40px',
                    height: '40px'
                  }}
                />
              )}
            </div>
          ) : (
            <CenteredLoadingAnimation light small />
          )}
        </VerticalCenteredRow>

        {/* Text */}
        <Column style={{ marginLeft: '8px' }}>
          <div className="fs-16 fw-bold" style={{ flex: '8' }}>
            {title || artistDisplay}
          </div>
          {title && (
            <div className="fs-12 fw-regular" style={{ color: gray4 }}>
              {artistDisplay}
            </div>
          )}
        </Column>
        {/* TODO Add this back */}
        {/* {helperText && (
          <Column style={{ width: '70px' }}>
            <TagButton backgroundColor={gray13} color={gray1}>
              {helperText}
            </TagButton>
          </Column>
        )} */}
      </VerticalCenteredRow>
      {subtitle && (
        <div className="dm-sans-font fw-bold fs-14" style={{ color: gray1, marginTop: '14px' }}>
          {subtitle}
        </div>
      )}
      <Column style={{ margin: '10px 0px' }}>{children}</Column>
    </Column>
  )
}

export const SongItem: FC<SongItemProps> = observer(({ songData, hideBottomBorder }) => {
  const [stemsLoaded, setStemsLoaded] = useState<boolean>(false)
  const { data: user } = useUser({ walletAddressOrId: songData.user.walletAddress })

  useEffect(() => {
    setStemsLoaded(false)
    ArpRegistry.getByIdsAsync(songData.subComponents).then(() => setStemsLoaded(true))
  }, [songData.subComponents])

  return (
    <ArtistMediaDropdown
      title={songData.metadata.title}
      helperText={`${songData.metadata.bpm} BPM`}
      artistProfile={user}
      artistEthAddress={songData.user.walletAddress}
      song={songData}
      hideBottomBorder={hideBottomBorder}
      subtitle="Stems"
    >
      {stemsLoaded ? (
        songData.subComponents
          .map((subComponent) => ArpRegistry.retrieveFromCache(subComponent))
          .map((stem) => <StemItem stem={stem} key={stem.id} isSubitem={true} />)
      ) : (
        <CenteredLoadingAnimation light small />
      )}
    </ArtistMediaDropdown>
  )
})

interface ArtistSamplesItemProps {
  samples: number[]
  artistProfile: User
  artistEthAddress: string
  hideBottomBorder?: boolean
  isActive?: boolean
  setActiveIndex: (index: number) => void
  index: number
}

export const ArtistSamplesItem: FC<ArtistSamplesItemProps> = observer(
  ({
    samples,
    artistProfile,
    artistEthAddress,
    hideBottomBorder,
    isActive,
    setActiveIndex,
    index
  }) => {
    const loadedSamples: IArpMedia[] = samples.map((index) => ArpRegistry.retrieveFromCache(index))

    return (
      <ArtistMediaDropdown
        artistProfile={artistProfile}
        artistEthAddress={artistEthAddress}
        hideBottomBorder={hideBottomBorder}
      >
        <SampleList
          samples={loadedSamples}
          defaultIsOpen
          defaultIsActive={isActive}
          onItemClick={() => setActiveIndex(index)}
        />
      </ArtistMediaDropdown>
    )
  }
)

export default observer(ArtistMediaDropdown)
